import type { ReactNode, Ref } from "react";
import Link from "next/link";
import { Breadcrumbs, type BreadcrumbItemType } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { MobileMapToggle } from "@/components/location/mobile-map-toggle";
import { BelowLgOnly } from "@/components/responsive/breakpoint-gate";
import { quoteHrefFor } from "@/lib/listings/category-facets";

export function CatalogueShell({
  header,
  heading,
  filterBar,
  mapColumn,
  paneRef,
  after,
  children,
}: {
  header?: ReactNode;
  heading?: ReactNode;
  filterBar?: ReactNode;
  mapColumn?: ReactNode;
  paneRef?: Ref<HTMLElement>;
  after?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col bg-surface">
      {header}
      <div className="flex">
        {mapColumn}
        <div className="flex min-w-0 flex-1 flex-col">
          {heading}
          {filterBar ? (
            <div className="sticky top-16 z-30 max-h-[calc(100dvh-4rem)] overflow-y-auto bg-surface">{filterBar}</div>
          ) : null}
          <section
            ref={paneRef}
            className="@container flex min-w-0 flex-1 flex-col [overflow-anchor:none]"
          >
            {children}
          </section>
        </div>
      </div>
      {after}
    </div>
  );
}

const NAVBAR_HEIGHT_PX = 64;

export function scrollCatalogueToTop(pane: HTMLElement | null) {
  if (!pane) return;
  const stickyBar = pane.previousElementSibling;
  const stickyBottom =
    stickyBar instanceof HTMLElement
      ? stickyBar.getBoundingClientRect().bottom
      : NAVBAR_HEIGHT_PX;
  const offset = pane.getBoundingClientRect().top - stickyBottom;
  if (offset < 0) window.scrollBy({ top: offset });
}

export function CatalogueHeaderBar({
  breadcrumbs,
  action,
}: {
  breadcrumbs: BreadcrumbItemType[];
  action?: ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-[100rem] shrink-0 items-center justify-between gap-3 px-4 pt-4 sm:px-8">
      <Breadcrumbs items={breadcrumbs} />
      {action}
    </div>
  );
}

export function CatalogueQuoteCard({ productSlug }: { productSlug: string }) {
  return (
    <div className="mx-auto w-full max-w-[100rem] px-4 py-10 @3xl:px-8">
      <div className="flex flex-col gap-4 border-t border-border-subtle pt-8 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="font-display text-xl font-semibold text-foreground">Don&apos;t see the size, grade or amount you need?</h2>
          <p className="text-base text-muted-foreground">Tell us what you need and we&apos;ll quote it delivered.</p>
        </div>
        <Button asChild variant="chip" size="chip" className="shrink-0 self-start sm:self-auto">
          <Link href={quoteHrefFor(productSlug)}>Ask for a quote</Link>
        </Button>
      </div>
    </div>
  );
}

export function CatalogueMapColumn({ children }: { children: ReactNode }) {
  return (
    <aside
      aria-label="Listings on map"
      className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-1/3 max-w-120 shrink-0 self-start border-r border-border lg:block [:root[data-ask-panel=open]_&]:xl:hidden"
    >
      {children}
    </aside>
  );
}

export function CatalogueMobileMap({
  overlayTitle,
  open,
  onOpenChange,
  children,
}: {
  overlayTitle: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <BelowLgOnly>
      <MobileMapToggle
        pillLabel="Map"
        overlayTitle={overlayTitle}
        open={open}
        onOpenChange={onOpenChange}
        className="h-[320px] shrink-0 border-b border-border"
      >
        {children}
      </MobileMapToggle>
    </BelowLgOnly>
  );
}

export function CatalogueResultsHeading({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-[100rem] flex-wrap items-end justify-between gap-x-4 gap-y-2 px-4 pt-3 pb-1 @3xl:px-8 sm:px-8">
      <div className="min-w-0 space-y-1">
        <h1 className="font-display text-[1.625rem] leading-tight font-semibold tracking-tight text-foreground sm:text-[2rem]">{title}</h1>
        <p className="text-base text-muted-foreground tabular-nums" aria-live="polite">
          {subtitle}
        </p>
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function catalogueGridClassName(): string {
  return "mx-auto grid w-full max-w-[100rem] grid-cols-1 px-4 py-2 @xl:grid-cols-2 @xl:gap-x-6 @xl:gap-y-8 @xl:py-6 @3xl:px-8 @4xl:grid-cols-3 @6xl:grid-cols-4";
}
